import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  InputAdornment,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  GetApp as ExportIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  QueryConstraint,
  startAfter,
  DocumentSnapshot,
} from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { NotificationType, NotificationPriority, UserNotification } from '../../../types/user';

interface NotificationHistoryItem extends UserNotification {
  recipientName?: string;
  recipientEmail?: string;
}

interface SearchFilters {
  searchText: string;
  type: NotificationType | 'all';
  priority: NotificationPriority | 'all';
  read: 'all' | 'read' | 'unread';
  archived: 'all' | 'archived' | 'active';
  dateFrom: Date | null;
  dateTo: Date | null;
  userId: string;
}

/**
 * NotificationHistory Page
 * Comprehensive notification history with advanced search, filtering, pagination, and export
 */
const NotificationHistory: React.FC = () => {
  const navigate = useNavigate();

  // State - Data
  const [notifications, setNotifications] = useState<NotificationHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // State - Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [lastDoc, setLastDoc] = useState<DocumentSnapshot | null>(null);

  // State - Filters
  const [filters, setFilters] = useState<SearchFilters>({
    searchText: '',
    type: 'all',
    priority: 'all',
    read: 'all',
    archived: 'all',
    dateFrom: null,
    dateTo: null,
    userId: '',
  });

  // State - Filter Dialog
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);

  // State - Detail Dialog
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<NotificationHistoryItem | null>(
    null
  );

  // State - Export
  const [exporting, setExporting] = useState(false);

  /**
   * Load notifications on mount and when filters change
   */
  useEffect(() => {
    loadNotifications();
  }, [page, rowsPerPage]);

  /**
   * Load notifications from Firestore
   */
  const loadNotifications = async () => {
    setLoading(true);
    setError(null);

    try {
      const constraints: QueryConstraint[] = [];

      // Apply filters
      if (filters.type !== 'all') {
        constraints.push(where('type', '==', filters.type));
      }

      if (filters.priority !== 'all') {
        constraints.push(where('priority', '==', filters.priority));
      }

      if (filters.read === 'read') {
        constraints.push(where('read', '==', true));
      } else if (filters.read === 'unread') {
        constraints.push(where('read', '==', false));
      }

      if (filters.archived === 'archived') {
        constraints.push(where('archived', '==', true));
      } else if (filters.archived === 'active') {
        constraints.push(where('archived', '==', false));
      }

      if (filters.userId) {
        constraints.push(where('userId', '==', filters.userId));
      }

      if (filters.dateFrom) {
        constraints.push(where('createdAt', '>=', filters.dateFrom));
      }

      if (filters.dateTo) {
        constraints.push(where('createdAt', '<=', filters.dateTo));
      }

      // Add ordering and pagination
      constraints.push(orderBy('createdAt', 'desc'));
      constraints.push(limit(rowsPerPage));

      // Handle pagination
      if (page > 0 && lastDoc) {
        constraints.push(startAfter(lastDoc));
      }

      const q = query(collection(db, 'notifications'), ...constraints);
      const snapshot = await getDocs(q);

      // Get last document for pagination
      if (snapshot.docs.length > 0) {
        setLastDoc(snapshot.docs[snapshot.docs.length - 1]);
      }

      // Map documents to notifications
      const notificationData = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          readAt: data.readAt?.toDate(),
          archivedAt: data.archivedAt?.toDate(),
        } as NotificationHistoryItem;
      });

      // Load user data for each notification
      const enrichedNotifications = await enrichWithUserData(notificationData);

      setNotifications(enrichedNotifications);

      // Get total count (approximate for large datasets)
      setTotalCount(snapshot.docs.length < rowsPerPage ? page * rowsPerPage + snapshot.docs.length : (page + 2) * rowsPerPage);
    } catch (err: any) {
      console.error('Error loading notifications:', err);
      setError(err.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Enrich notifications with user data
   */
  const enrichWithUserData = async (
    notifications: NotificationHistoryItem[]
  ): Promise<NotificationHistoryItem[]> => {
    const userIds = Array.from(new Set(notifications.map((n) => n.userId)));
    const userMap = new Map<string, { name: string; email: string }>();

    // Fetch user data in batches
    for (const userId of userIds) {
      try {
        const userDoc = await getDocs(
          query(collection(db, 'users'), where('__name__', '==', userId), limit(1))
        );
        if (!userDoc.empty) {
          const userData = userDoc.docs[0].data();
          userMap.set(userId, {
            name: userData.displayName || 'Unknown User',
            email: userData.email || '',
          });
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
      }
    }

    // Enrich notifications with user data
    return notifications.map((notification) => {
      const userData = userMap.get(notification.userId);
      return {
        ...notification,
        recipientName: userData?.name,
        recipientEmail: userData?.email,
      };
    });
  };

  /**
   * Handle search
   */
  const handleSearch = () => {
    setPage(0);
    setLastDoc(null);
    loadNotifications();
  };

  /**
   * Handle filter change
   */
  const handleFilterChange = (key: keyof SearchFilters, value: any) => {
    setFilters({ ...filters, [key]: value });
  };

  /**
   * Handle reset filters
   */
  const handleResetFilters = () => {
    setFilters({
      searchText: '',
      type: 'all',
      priority: 'all',
      read: 'all',
      archived: 'all',
      dateFrom: null,
      dateTo: null,
      userId: '',
    });
    setPage(0);
    setLastDoc(null);
  };

  /**
   * Handle page change
   */
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  /**
   * Handle rows per page change
   */
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setLastDoc(null);
  };

  /**
   * Handle view details
   */
  const handleViewDetails = (notification: NotificationHistoryItem) => {
    setSelectedNotification(notification);
    setDetailDialogOpen(true);
  };

  /**
   * Handle export
   */
  const handleExport = async () => {
    setExporting(true);
    try {
      // Export all notifications (not just current page)
      const allConstraints: QueryConstraint[] = [];

      // Apply same filters as current view
      if (filters.type !== 'all') {
        allConstraints.push(where('type', '==', filters.type));
      }
      if (filters.priority !== 'all') {
        allConstraints.push(where('priority', '==', filters.priority));
      }
      if (filters.read === 'read') {
        allConstraints.push(where('read', '==', true));
      } else if (filters.read === 'unread') {
        allConstraints.push(where('read', '==', false));
      }
      if (filters.archived === 'archived') {
        allConstraints.push(where('archived', '==', true));
      } else if (filters.archived === 'active') {
        allConstraints.push(where('archived', '==', false));
      }
      if (filters.userId) {
        allConstraints.push(where('userId', '==', filters.userId));
      }
      if (filters.dateFrom) {
        allConstraints.push(where('createdAt', '>=', filters.dateFrom));
      }
      if (filters.dateTo) {
        allConstraints.push(where('createdAt', '<=', filters.dateTo));
      }

      allConstraints.push(orderBy('createdAt', 'desc'));
      allConstraints.push(limit(10000)); // Max export limit

      const q = query(collection(db, 'notifications'), ...allConstraints);
      const snapshot = await getDocs(q);

      const exportData = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          ID: doc.id,
          Type: data.type,
          Priority: data.priority || 'normal',
          Title: data.title,
          Message: data.message,
          'User ID': data.userId,
          Read: data.read ? 'Yes' : 'No',
          Archived: data.archived ? 'Yes' : 'No',
          'Created At': data.createdAt?.toDate().toISOString() || '',
          'Read At': data.readAt?.toDate().toISOString() || '',
        };
      });

      // Convert to CSV
      const headers = Object.keys(exportData[0] || {});
      const csv = [
        headers.join(','),
        ...exportData.map((row) =>
          headers.map((header) => `"${(row as any)[header] || ''}"`).join(',')
        ),
      ].join('\n');

      // Download
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `notification-history-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error exporting notifications:', err);
      setError(err.message || 'Failed to export notifications');
    } finally {
      setExporting(false);
    }
  };

  /**
   * Format notification type for display
   */
  const formatType = (type: NotificationType): string => {
    return type
      .toString()
      .split('_')
      .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  /**
   * Get priority color
   */
  const getPriorityColor = (
    priority?: NotificationPriority
  ): 'error' | 'warning' | 'info' | 'default' => {
    switch (priority) {
      case NotificationPriority.CRITICAL:
        return 'error';
      case NotificationPriority.HIGH:
        return 'warning';
      case NotificationPriority.NORMAL:
        return 'info';
      case NotificationPriority.LOW:
        return 'default';
      default:
        return 'default';
    }
  };

  /**
   * Get all notification types
   */
  const getAllNotificationTypes = (): NotificationType[] => {
    return Object.values(NotificationType);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <Tooltip title="Back to Notification Dashboard">
            <IconButton onClick={() => navigate('/admin/notifications')} sx={{ mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="h4" sx={{ fontWeight: 700, flexGrow: 1 }}>
            Notification History
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Refresh">
              <IconButton onClick={loadNotifications} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Search and Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                placeholder="Search by title or message..."
                value={filters.searchText}
                onChange={(e) => handleFilterChange('searchText', e.target.value)}
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={filters.type}
                  label="Type"
                  onChange={(e) => handleFilterChange('type', e.target.value)}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  {getAllNotificationTypes().map((type) => (
                    <MenuItem key={type} value={type}>
                      {formatType(type)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={filters.priority}
                  label="Priority"
                  onChange={(e) => handleFilterChange('priority', e.target.value)}
                >
                  <MenuItem value="all">All Priorities</MenuItem>
                  <MenuItem value={NotificationPriority.CRITICAL}>Critical</MenuItem>
                  <MenuItem value={NotificationPriority.HIGH}>High</MenuItem>
                  <MenuItem value={NotificationPriority.NORMAL}>Normal</MenuItem>
                  <MenuItem value={NotificationPriority.LOW}>Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.read}
                  label="Status"
                  onChange={(e) => handleFilterChange('read', e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="read">Read</MenuItem>
                  <MenuItem value="unread">Unread</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                startIcon={<FilterListIcon />}
                onClick={() => setFilterDialogOpen(true)}
                fullWidth
              >
                More Filters
              </Button>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button variant="contained" onClick={handleSearch} disabled={loading}>
              {loading ? 'Searching...' : 'Search'}
            </Button>
            <Button onClick={handleResetFilters}>Reset</Button>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="outlined"
              startIcon={exporting ? <CircularProgress size={16} /> : <ExportIcon />}
              onClick={handleExport}
              disabled={exporting || notifications.length === 0}
            >
              {exporting ? 'Exporting...' : 'Export CSV'}
            </Button>
          </Box>
        </Paper>

        {/* Results Table */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date/Time</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Recipient</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                )}

                {!loading && notifications.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No notifications found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                {!loading &&
                  notifications.map((notification) => (
                    <TableRow key={notification.id} hover>
                      <TableCell>
                        <Typography variant="body2">
                          {format(notification.createdAt, 'MMM d, yyyy')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {format(notification.createdAt, 'h:mm a')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                          {formatType(notification.type)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={notification.priority || 'normal'}
                          size="small"
                          color={getPriorityColor(notification.priority)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 300 }}>
                          {notification.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {notification.recipientName || notification.userId}
                        </Typography>
                        {notification.recipientEmail && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {notification.recipientEmail}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          {notification.read && <Chip label="Read" size="small" color="success" />}
                          {notification.archived && (
                            <Chip label="Archived" size="small" color="default" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => handleViewDetails(notification)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Paper>

        {/* Advanced Filters Dialog */}
        <Dialog
          open={filterDialogOpen}
          onClose={() => setFilterDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Advanced Filters</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Date From"
                    type="date"
                    value={filters.dateFrom ? format(filters.dateFrom, 'yyyy-MM-dd') : ''}
                    onChange={(e) => handleFilterChange('dateFrom', e.target.value ? new Date(e.target.value) : null)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Date To"
                    type="date"
                    value={filters.dateTo ? format(filters.dateTo, 'yyyy-MM-dd') : ''}
                    onChange={(e) => handleFilterChange('dateTo', e.target.value ? new Date(e.target.value) : null)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Archived</InputLabel>
                    <Select
                      value={filters.archived}
                      label="Archived"
                      onChange={(e) => handleFilterChange('archived', e.target.value)}
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="active">Active Only</MenuItem>
                      <MenuItem value="archived">Archived Only</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="User ID"
                    value={filters.userId}
                    onChange={(e) => handleFilterChange('userId', e.target.value)}
                    size="small"
                    fullWidth
                    placeholder="Filter by specific user ID"
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFilterDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={() => {
                setFilterDialogOpen(false);
                handleSearch();
              }}
              variant="contained"
            >
              Apply
            </Button>
          </DialogActions>
        </Dialog>

        {/* Notification Detail Dialog */}
        <Dialog
          open={detailDialogOpen}
          onClose={() => setDetailDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Notification Details</DialogTitle>
          <DialogContent dividers>
            {selectedNotification && (
              <Box>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Type
                    </Typography>
                    <Typography variant="body1">
                      {formatType(selectedNotification.type)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Priority
                    </Typography>
                    <Chip
                      label={selectedNotification.priority || 'normal'}
                      size="small"
                      color={getPriorityColor(selectedNotification.priority)}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Status
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {selectedNotification.read && (
                        <Chip label="Read" size="small" color="success" />
                      )}
                      {selectedNotification.archived && (
                        <Chip label="Archived" size="small" color="default" />
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Title
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {selectedNotification.title}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Message
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedNotification.message}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Recipient
                    </Typography>
                    <Typography variant="body1">
                      {selectedNotification.recipientName || 'Unknown'}
                    </Typography>
                    {selectedNotification.recipientEmail && (
                      <Typography variant="body2" color="text.secondary">
                        {selectedNotification.recipientEmail}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Created At
                    </Typography>
                    <Typography variant="body2">
                      {format(selectedNotification.createdAt, 'PPpp')}
                    </Typography>
                  </Grid>

                  {selectedNotification.readAt && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Read At
                      </Typography>
                      <Typography variant="body2">
                        {format(selectedNotification.readAt, 'PPpp')}
                      </Typography>
                    </Grid>
                  )}

                  {selectedNotification.actionUrl && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Action URL
                      </Typography>
                      <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                        {selectedNotification.actionUrl}
                      </Typography>
                    </Grid>
                  )}

                  {selectedNotification.data && Object.keys(selectedNotification.data).length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Additional Data
                      </Typography>
                      <Paper
                        variant="outlined"
                        sx={{ p: 1, bgcolor: 'grey.50', fontFamily: 'monospace', fontSize: 12 }}
                      >
                        <pre>{JSON.stringify(selectedNotification.data, null, 2)}</pre>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Container>
  );
};

export default NotificationHistory;
